import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, DestroyRef, inject, input, signal, ChangeDetectionStrategy, OnInit } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { ActivatedRoute, Router } from '@angular/router';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { AssistantKey, AssistantType } from '@vendasta/protos/ai_assistants/v1alpha1';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, startWith, switchMap } from 'rxjs/operators';
import { AiAssistantI18nModule } from '../../assets/i18n/ai-assistant-i18n.module';
import { evaluatorTestType } from '../../core/utils/success-evaluators';
import {
  JOB_QUERY_PARAM,
  JobDraft,
  JobStat,
  JobsToBeDoneService,
  RESULT_QUERY_PARAM,
  mergeQueryParams,
} from './jobs-to-be-done.service';
import {
  JobCreateModalComponent,
  JobCreateModalData,
  JobCreateModalResult,
} from './job-create-modal/job-create-modal.component';
import {
  JobDetailModalComponent,
  JobDetailModalData,
  JobDetailModalResult,
} from './job-detail-modal/job-detail-modal.component';

// Status the gateway returns for a gRPC FAILED_PRECONDITION, which is what the API
// returns once an account holds as many jobs as it may define. A rejected job is an
// invalid argument and arrives as a 400, so the two do not collide.
const HTTP_PRECONDITION_FAILED = 412;

/** Whether a save failed because the account is at its limit, not because the job was rejected. */
function isJobLimitReached(err: unknown): boolean {
  return err instanceof HttpErrorResponse && err.status === HTTP_PRECONDITION_FAILED;
}

/** Where a job's pass rate sits against the rate it is held to. */
export type TargetStanding = 'on-target' | 'below-target';

/**
 * Compares a job's pass rate to its target at the one-decimal percent the job's
 * detail shows, so a rate that displays as its target reads as on target.
 */
export function targetStanding(rate: number, target: number): TargetStanding {
  return Math.round(rate * 1000) >= Math.round(target * 1000) ? 'on-target' : 'below-target';
}

/**
 * A row in the list. A null success rate means nothing has been scored against the
 * job, which is the case for one the user has just written.
 */

/** Rows shown before the user asks for the rest. */
export const INITIAL_VISIBLE_JOBS = 3;

type JobsState = {
  status: 'loading' | 'loaded' | 'error';
  jobs: JobStat[];
};

@Component({
  selector: 'ai-jobs-to-be-done',
  imports: [
    MatButtonModule,
    MatCardModule,
    GalaxyLoadingSpinnerModule,
    GalaxyAlertModule,
    GalaxyBadgeModule,
    AiAssistantI18nModule,
  ],
  templateUrl: './jobs-to-be-done.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './jobs-to-be-done.component.scss',
})
export class JobsToBeDoneComponent implements OnInit {
  private readonly jobsToBeDoneService = inject(JobsToBeDoneService);
  private readonly dialog = inject(MatDialog);
  private readonly confirmationModal = inject(OpenConfirmationModalService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  /** Read-only: the link is restored from this snapshot; writes go through mergeQueryParams. */
  private readonly route = inject(ActivatedRoute);

  readonly assistantKey = input.required<AssistantKey>();
  readonly assistantType = input<AssistantType>();

  protected readonly standing = targetStanding;

  /** Re-reads the list after a write, so a saved job arrives with its stored id. */
  private readonly reload = new BehaviorSubject<void>(undefined);

  private readonly state = toSignal(
    combineLatest([
      toObservable(this.assistantKey).pipe(
        // The key input can arrive as a new object with the same value (e.g. a
        // parent computed that re-emits on unrelated changes); only refetch when
        // the assistant actually changes.
        distinctUntilChanged((a, b) => a.id === b.id && JSON.stringify(a.namespace) === JSON.stringify(b.namespace)),
      ),
      this.reload,
    ]).pipe(
      switchMap(([key]) =>
        this.jobsToBeDoneService.listJobsToBeDone(key).pipe(
          map((jobs): JobsState => ({ status: 'loaded', jobs })),
          startWith<JobsState>({ status: 'loading', jobs: [] }),
          catchError(() => of<JobsState>({ status: 'error', jobs: [] })),
        ),
      ),
    ),
    { initialValue: { status: 'loading', jobs: [] } as JobsState },
  );

  /** Whether the user has asked to see past the first few rows. */
  private readonly expanded = signal(false);

  /**
   * The link the card was loaded with, read before anything writes to the URL and
   * consumed by the first list that arrives.
   */
  ngOnInit(): void {
    this.openLinkedJob();
  }

  /** The last write action's failure, if any. Cleared when another one starts. */
  protected readonly actionError = signal<'limit' | 'generic' | 'delete' | null>(null);
  protected readonly saving = signal(false);

  /**
   * Global jobs first, then the ones belonging to this assistant. The sort is stable,
   * so within each group the endpoint's display order survives.
   */
  readonly jobs = computed<JobStat[]>(() =>
    [...this.state().jobs].sort((a, b) => Number(!!a.custom) - Number(!!b.custom)),
  );
  readonly loading = computed(() => this.state().status === 'loading');
  readonly errored = computed(() => this.state().status === 'error');

  protected readonly visibleJobs = computed<JobStat[]>(() => {
    const jobs = this.jobs();
    return this.expanded() ? jobs : jobs.slice(0, INITIAL_VISIBLE_JOBS);
  });

  protected readonly hiddenCount = computed(() => this.jobs().length - this.visibleJobs().length);

  /** Days of scored conversations the rates cover, as the stats report it; 0 until they have loaded. */
  public readonly evaluationWindowDays = computed(() => this.jobs()[0]?.evaluationWindowDays ?? 0);

  protected showMore(): void {
    this.expanded.set(true);
  }

  private readLink(): { jobId: string; resultTestRunId?: string } | null {
    const params = this.route.snapshot.queryParamMap;
    const jobId = params.get(JOB_QUERY_PARAM);
    return jobId ? { jobId, resultTestRunId: params.get(RESULT_QUERY_PARAM) ?? undefined } : null;
  }

  /**
   * Opens the job a link named. Read by id rather than found in the list, so the
   * modal does not wait on the card's own fetch and does not depend on what that
   * fetch returned. A job that no longer applies drops its params rather than
   * leaving a link that opens nothing.
   */
  private openLinkedJob(): void {
    const link = this.readLink();
    if (!link) return;

    this.jobsToBeDoneService
      .getJob(this.assistantKey(), link.jobId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (row) => {
          if (!row) {
            this.clearLinkParams();
            return;
          }
          // Expanding keeps the opened job on screen behind the modal when it sits
          // past the cut-off.
          this.showMore();
          this.openDetail(row, link.resultTestRunId);
        },
        // A failure says nothing about whether the job exists, so the params stay
        // and a retry can still resolve the link.
        error: () => undefined,
      });
  }

  private clearLinkParams(): void {
    mergeQueryParams(this.router, { [JOB_QUERY_PARAM]: null, [RESULT_QUERY_PARAM]: null });
  }

  openDetail(row: JobStat, openResultTestRunId?: string): void {
    mergeQueryParams(this.router, { [JOB_QUERY_PARAM]: row.testCase.id });
    this.dialog
      .open<JobDetailModalComponent, JobDetailModalData, JobDetailModalResult>(JobDetailModalComponent, {
        data: {
          stat: row,
          assistantKey: this.assistantKey(),
          openResultTestRunId,
        },
        maxHeight: '90vh',
        autoFocus: false,
      })
      .afterClosed()
      // The overlay outlives this component, so without this a close after the user
      // has navigated away would clear params on whatever page they are now on.
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.clearLinkParams();
        if (result === 'edit') this.openEdit(row);
        if (result === 'delete') this.confirmDelete(row);
      });
  }

  openCreate(): void {
    this.openForm(null).subscribe((result) => {
      if (!result) return;
      this.save({
        name: result.name,
        scenario: result.scenario,
        expectation: result.expectation,
        successEvaluationTargetRate: result.successEvaluationTargetRate,
      });
    });
  }

  /** Deleting is irreversible and loses the job's scoring history, so it is confirmed. */
  private confirmDelete(row: JobStat): void {
    this.confirmationModal
      .openModal({
        type: 'warn',
        title: 'AI_ASSISTANT.TESTING.JOBS_TO_BE_DONE.DELETE_CONFIRMATION.TITLE',
        message: 'AI_ASSISTANT.TESTING.JOBS_TO_BE_DONE.DELETE_CONFIRMATION.MESSAGE',
        confirmButtonText: 'AI_ASSISTANT.SHARED.DELETE',
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed: boolean) => {
        if (!confirmed) return;
        this.deleteJob(row);
      });
  }

  private deleteJob(row: JobStat): void {
    this.actionError.set(null);
    this.saving.set(true);
    this.jobsToBeDoneService.deleteJob(this.assistantKey(), row.testCase.id).subscribe({
      next: () => {
        this.saving.set(false);
        this.reload.next();
      },
      error: () => {
        this.saving.set(false);
        this.actionError.set('delete');
      },
    });
  }

  private openEdit(row: JobStat): void {
    this.openForm({
      name: row.testCase.name,
      scenario: row.testCase.scenario,
      expectation: row.testCase.expectation,
      successEvaluationTargetRate: row.targetRate,
      isSuccessEvaluator: true,
      successRate: row.successRate,
      evaluatedCount: row.evaluatedCount,
      passedCount: row.passedCount,
      evaluationWindowDays: row.evaluationWindowDays,
    }).subscribe((result) => {
      if (!result) return;
      this.save({
        id: row.testCase.id,
        name: result.name,
        scenario: result.scenario,
        expectation: result.expectation,
        successEvaluationTargetRate: result.successEvaluationTargetRate,
      });
    });
  }

  private save(draft: JobDraft): void {
    this.saving.set(true);
    this.jobsToBeDoneService.saveJob(this.assistantKey(), draft, evaluatorTestType(this.assistantType())).subscribe({
      next: () => {
        this.saving.set(false);
        // A saved job lists with the assistant's own, which can sit past the
        // cut-off. Expanding keeps what was just written on screen.
        this.showMore();
        this.reload.next();
      },
      error: (err: unknown) => {
        this.saving.set(false);
        this.actionError.set(isJobLimitReached(err) ? 'limit' : 'generic');
      },
    });
  }

  private openForm(job: JobCreateModalData | null): Observable<JobCreateModalResult | undefined> {
    this.actionError.set(null);
    return this.dialog
      .open<JobCreateModalComponent, JobCreateModalData | null, JobCreateModalResult>(JobCreateModalComponent, {
        data: job,
        autoFocus: false,
      })
      .afterClosed();
  }
}
